import React, { useEffect, useState } from "react";
import { Apple, Smartphone, RefreshCw, Loader2 } from "lucide-react";
import { api } from "../api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

// MASTER: live App Store / Google Play versions + optional manual override.
export const StoreVersionsCard = ({ t, lang }) => {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ov, setOv] = useState({ ios_override: "", android_override: "" });

  const load = (d) => {
    setDoc(d);
    setOv({ ios_override: d.ios_override || "", android_override: d.android_override || "" });
  };
  useEffect(() => { api.get("/app/store-versions").then((r) => load(r.data)).catch(() => {}); }, []);

  const refresh = async () => {
    setBusy(true);
    try { load((await api.post("/app/store-versions/refresh")).data); toast.success(t("store_versions_refreshed")); }
    catch (e) { toast.error(t("something_wrong")); }
    finally { setBusy(false); }
  };
  const saveOverride = async () => {
    try { load((await api.patch("/app/store-versions", ov)).data); toast.success(t("saved")); }
    catch (e) { toast.error(t("something_wrong")); }
  };

  if (!doc) return null;
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString(lang === "pt" ? "pt-BR" : "en-US") : "—");
  const rows = [
    { key: "ios", Icon: Apple, label: "App Store" },
    { key: "android", Icon: Smartphone, label: "Google Play" },
  ];
  return (
    <Card className="p-3" data-testid="store-versions-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold text-sm">{t("store_versions_title")}</h3>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={refresh} disabled={busy} data-testid="store-versions-refresh">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground mb-2 leading-relaxed">{t("store_versions_hint")}</p>
      <div className="space-y-1.5">
        {rows.map(({ key, Icon, label }) => (
          <div key={key} className="flex items-center gap-2 text-xs" data-testid={`store-version-${key}`}>
            <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="w-20 shrink-0">{label}</span>
            <span className="font-mono font-semibold" data-testid={`store-version-${key}-value`}>{doc[key] || "—"}</span>
            {doc[`${key}_error`] && !doc[key] && <span className="text-[10px] text-destructive truncate">{t("store_lookup_failed")}</span>}
            <Input
              value={ov[`${key}_override`]}
              onChange={(e) => setOv({ ...ov, [`${key}_override`]: e.target.value })}
              placeholder={t("store_override_ph")}
              className="h-7 text-xs w-24 ml-auto"
              data-testid={`store-override-${key}`}
            />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-[10px] text-muted-foreground">{t("store_checked_at")}: {fmt(doc.checked_at)}</span>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={saveOverride} data-testid="store-override-save">{t("save")}</Button>
      </div>
    </Card>
  );
};

export const UserAppVersion = ({ a, store }) => {
  if (!a.app_version) return null;
  const latest = store ? (store[`${a.app_platform}_override`] || store[a.app_platform]) : null;
  const num = (v) => (v || "").split(/\D+/).filter(Boolean).map(Number);
  const outdated = (() => {
    if (!latest) return false;
    const x = num(a.app_version), y = num(latest);
    for (let i = 0; i < Math.max(x.length, y.length); i++) {
      const d = (x[i] || 0) - (y[i] || 0);
      if (d !== 0) return d < 0;
    }
    return false;
  })();
  const Icon = a.app_platform === "ios" ? Apple : Smartphone;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[9px] font-mono shrink-0 ${outdated ? "text-amber-600" : "text-muted-foreground/70"}`}
      title={a.app_version_at ? new Date(a.app_version_at).toLocaleString() : ""}
      data-testid={`user-app-version-${a.id}`}
    >
      <Icon className="h-2.5 w-2.5" />{a.app_version}{a.app_build ? ` (${a.app_build})` : ""}
    </span>
  );
};
